/**
 * bedSurface — where a dorm bed's sleeping surface actually is.
 *
 * This exists because the answer lived in two places that disagreed:
 *
 *   - `dormBedRenderers.js` hardcoded a rail-bottom height per bed type in its
 *     dispatch (9 / 24 / 31 / 57 in) and derived the mattress top from private
 *     constants, and
 *   - `dorm-optimizer-v2/constants.js` kept UNDERBED_CLEARANCE_INCHES
 *     (7 / 15 / 32 / 55 / 13) for under-bed storage.
 *
 * They differ by up to 11 inches (standard: 24 vs 15; bunk: 24 vs 13). Nothing
 * compared them, because nothing needed both — until a planner wanted to seat
 * a stuffed animal ON a mattress, at which point guessing from the wrong table
 * puts it most of a foot above the duvet.
 *
 * The RENDERER's numbers are authoritative here: they are what the geometry is
 * actually drawn from, so they are what a decor object must agree with. The
 * optimizer's table describes the clear opening UNDER the bed and is a
 * different measurement of a different thing — deliberately left alone.
 */

const IN_TO_M = 0.0254;

/** Deck slab thickness (frame rail top → deck top). */
const DECK_H_M = 0.5 * IN_TO_M;
/** Mattress thickness (deck top → sleeping surface). */
const MATTRESS_H_M = 8 * IN_TO_M;
/** Frame inset: the mattress is this much narrower/shallower than the bed box. */
const FRAME_INSET_M = 4 * IN_TO_M;

/**
 * The pillow's footprint along the bed, and the gap between it and the head
 * end. Shared for the same reason the rail heights are: anything seated ON a
 * mattress has to know where the pillow already is, and re-deriving that from
 * a copied number is how the rail heights drifted in the first place.
 * A standard bed pillow is 20 x 26in industry-wide.
 */
const PILLOW_DEPTH_M = 20 * IN_TO_M;
const PILLOW_HEAD_GAP_M = 1.5 * IN_TO_M;

/** The accent cushion that leans back against the pillow. */
const ACCENT_DEPTH_M = 15 * IN_TO_M;
/** How far the cushion rides up the pillow, as a fraction of their half-depths. */
const ACCENT_OVERLAP = 0.6;

/** Breathing room between the bedding and anything seated beside it. */
const BED_DECOR_GAP_M = 1 * IN_TO_M;

/**
 * The soft layers stacked on the mattress. Anything that SITS on a made bed
 * rests on these, not on the mattress: seating a plush at the mattress plane
 * buries it to the neck in the duvet.
 */
const SHEET_OVER_M = 0.6 * IN_TO_M; // fitted sheet above the mattress face
const DUVET_THICKNESS_M = 2.75 * IN_TO_M; // duvet, underside on the sheet

/**
 * Depth from the mattress head edge already spoken for by the bedding. Decor
 * goes FOOT-ward of this.
 *
 * Derived, not estimated. The first version added a flat 4in for the accent
 * cushion, which under-counted the real reach by exactly 4in and left plushes
 * clipping through it — the cushion is 15in deep and only 60% overlapped, so
 * it extends well past the pillow it leans on.
 */
/**
 * Depth from the mattress head edge to the pillow's centre. The RENDERER
 * (`dormBedding/index.js`) seats the pillow from this too — sharing the
 * constants but not the formulas built on them is how the rail heights drifted
 * in the first place, one level up.
 */
function pillowCentreDepth() {
  return PILLOW_HEAD_GAP_M + PILLOW_DEPTH_M / 2;
}

/** Depth to the accent cushion's centre, leaning back against the pillow. */
function accentCentreDepth() {
  return (
    pillowCentreDepth() +
    (PILLOW_DEPTH_M / 2 + ACCENT_DEPTH_M / 2) * ACCENT_OVERLAP
  );
}

function pillowZoneDepth() {
  return accentCentreDepth() + ACCENT_DEPTH_M / 2 + BED_DECOR_GAP_M;
}

/**
 * Height of the frame rail's underside above the bed's own floor plane, per
 * variant. THE RENDERER READS THESE — do not fork them.
 */
const RAIL_BOTTOM_INCHES = {
  low: 9,
  standard: 24,
  captain: 31,
  loft: 57,
};

/**
 * Bunks carry their OWN pair of rail heights per variant — they are not
 * derivable from the single-bed table. `bunk-loft` in particular is 36/62,
 * not the loft's 57: stacking two decks changes both.
 */
const BUNK_RAIL_INCHES = {
  low: { lower: 9, upper: 57 },
  standard: { lower: 24, upper: 57 },
  captain: { lower: 31, upper: 57 },
  loft: { lower: 36, upper: 62 },
};

/** Variant key from a full type key, e.g. `dorm-bed-loft-xl` → `loft`. */
function bedVariant(typeKey) {
  const m = /^dorm-(bed|bunk)-([a-z]+)-/.exec(String(typeKey || ''));
  if (!m) return null;
  const variant = m[2];
  return RAIL_BOTTOM_INCHES[variant] ? variant : null;
}

/** A bunk's two rail heights, or null for a single bed. */
function bunkRails(typeKey) {
  if (!isBunkType(typeKey)) return null;
  const variant = bedVariant(typeKey);
  return (variant && BUNK_RAIL_INCHES[variant]) || null;
}

/** True for a bunk (two decks) rather than a single bed. */
function isBunkType(typeKey) {
  return /^dorm-bunk-/.test(String(typeKey || ''));
}

/**
 * Height of the sleeping surface above the bed object's own floor plane
 * (i.e. above `center.y - dimensions.y / 2`). Null for a non-bed type.
 *
 * For a bunk this is the LOWER deck: it is the one a person can reach, and a
 * plush on the upper deck of a 76in bunk is above eye level and invisible.
 */
/**
 * Deck top above a given rail-bottom height, in meters. The renderer builds
 * its four rail stacks (single / loft / bunk upper / bunk lower) from these two
 * helpers rather than re-adding DECK_H_M and MATTRESS_H_M each time, so adding
 * a layer to the stack moves every consumer including this module's own
 * mattressTopOffset.
 */
function deckTopFromRail(railBottomM) {
  return railBottomM + DECK_H_M;
}

/** Sleeping surface above a given rail-bottom height, in meters. */
function mattressTopFromRail(railBottomM) {
  return deckTopFromRail(railBottomM) + MATTRESS_H_M;
}

function mattressTopOffset(typeKey) {
  const variant = bedVariant(typeKey);
  if (!variant) return null;
  const rails = bunkRails(typeKey);
  const rail = rails ? rails.lower : RAIL_BOTTOM_INCHES[variant];
  return mattressTopFromRail(rail * IN_TO_M);
}

/**
 * Height of the MADE bed's top surface above the bed object's own floor plane
 * — mattress plus sheet plus duvet. This is what decor rests on.
 *
 * Valid foot-ward of the turn-back (the duvet's head edge is inset 18in and
 * the turn-back covers the next 10in); `planBedDecor` seats past 30in, so it
 * is always on plain duvet.
 */
function beddingTopOffset(typeKey) {
  const mattress = mattressTopOffset(typeKey);
  if (mattress == null) return null;
  return mattress + SHEET_OVER_M + DUVET_THICKNESS_M;
}

/** True for any dorm bed or bunk type. */
function isBedType(typeKey) {
  return bedVariant(typeKey) !== null;
}

module.exports = {
  deckTopFromRail,
  mattressTopFromRail,
  pillowCentreDepth,
  accentCentreDepth,
  isBunkType,
  SHEET_OVER_M,
  DUVET_THICKNESS_M,
  beddingTopOffset,
  PILLOW_DEPTH_M,
  PILLOW_HEAD_GAP_M,
  ACCENT_DEPTH_M,
  ACCENT_OVERLAP,
  BED_DECOR_GAP_M,
  pillowZoneDepth,
  BUNK_RAIL_INCHES,
  bunkRails,
  DECK_H_M,
  MATTRESS_H_M,
  FRAME_INSET_M,
  RAIL_BOTTOM_INCHES,
  bedVariant,
  mattressTopOffset,
  isBedType,
};
